import { KNIGHT_EXEC_BLADE_HEIGHT_PX, knightStrikeFrontPx } from './exec_unique'
import { isWallHuntGame, wallHuntOverlapsPlayer } from './awoken_wall_hunt_game'
import { parryOneGains, parryOneOn, parryOneTailMs } from './parry_one'
import { bandOverlapsPlayer, hoverSpirit, normalParrySelfGainFor, recordSpiritTravel, spiritExpired, spiritOverlapsHeight, steerSpirit } from './awoken_rampage_v5'
import { stanceBiteReach } from './boss_body_envelope'
import { restoresAwokenRushContactStopForStudy } from './awoken_rush_study'
import { AWOKEN_BITE_FRONT_THIRD, awokenBiteStrike, awokenBiteUsesFrontBody, awokenFrontBodyReachesHeight, awokenMouthBiteFallbackForwardPx, awokenMouthPoint, awokenMouthReachesHeight, awokenMouthStrike, isAwokenMouthBiteHit, type AwokenMouthPoint } from './awoken_bite_mouth'
// ヒットのタイムライン (spec 2026-06-11-attack-system-hit-timeline-redesign)。
// 攻撃判定の唯一の置き場: ボス追従の melee ヒットと、凍結ゾーン (床ハザード) を
// 同じ ActiveHit として寿命管理し、毎tick プレイヤーと判定する。
//
// 大原則 (spec §1〜3):
//   1. 回避無敵はダメージを防ぐ。boss追従の一振りは消費し、zone/travelは消費しない。
//   2. パリィは「そのヒットだけ」を消費する (連撃の次の段は残る)。
//   3. 反撃可否のフラグは無い。生きたヒットの中に自分から入れば食らう (emergent)。
//   4. 回避の見切り反撃窓は「パリィ不可かつboss追従」のヒットだけで開く。
//   5. melee ヒットは「ボスがその攻撃の active 中」しか生きない (崩し/blink で中断したら消える)。

import type { ActiveHit, AttackDef, Boss, FxEvent, Player, ScreenShake, SfxId, StrikeProfile } from './types'
import { attachCannonTracking, steerCannonProjectile, cannonProjectileOverlapsHeight } from './executioner_cannon_tracking'
import { ARENA, arenaAnchorX, arenaBoundsFor, arenaWalls, BOSS, BOSS_BODY_WALL_INSET_PX, BOSS_EXECUTE_FX, bossBodyBoundsFor, COMBAT, PLAYER, PLAYER_DISPLAY_HEIGHT } from './balance'
import { playerIncomingDamage, playerMaxPosture, playerParryActiveMs } from './difficulty'
import { grabPlayer, isPlayerInIFrames, isPlayerParryActive, openCounterWindow, stunPlayer } from './player'
import { stepIFrameMs } from './player_locomotion_trial'
import { staggerBoss } from './boss'
import { bossPostureGraceSec, econ } from './econ'
import { GUARD, guardSelfPostureGain } from './guard'
import { getParryBreakLook } from './parry_break_fx'
import { getParryDownLook } from './parry_family_fx'
import { NO_POSE_MOTION, resolveContactProgress } from './boss_pose_timeline'
import { playerHurtboxOverlapsStrike, strikeWorldInterval } from './combat_geometry'
import { knightCrossStrikeFrontPx } from './knight_cross_arc'
import { bossPostureGainMul, chipFloor, parryGainHpMul } from './combat'
import { playlogRecv } from './playlog_hook'
import { brideFlurryNormalParrySelfGain } from './bride_flurry_trial'
import { resolveBossTimelinePose } from './sprites'
import { awokenMouthArtFrontPx } from './exec_awoken_reach'
import { BLADE_FINISHER_CATCH_STOP_MS } from './blade_finisher_v2'
import { armEncore, lifelineApplies, lifelineBossPosture, rememberStunSource } from './exec_bait'
import { EXEC_PARRY, EXEC_PARRY_SETTLE_MS, EXEC_PUSH, execParryAccepts, execParryBigGainOn, execParryBossPostureAfter, execParryPushPx, execParrySlideMs, execPushVersion, execStunActive, planExecPush, stunParryCatchMs, stunParryWindowAt } from './exec_parry'
import { pushWeightSpec, pushWeightVariant } from './exec_push_weight'
import { execSlowStopMs } from './exec_parry_slow_fx'
import { breakStopExtraMs } from './break_stop_fx'
import { tehonParryStopMs } from './parry_tehon_fx'
import { ukemiDustFx, ukemiGuardKind, ukemiGuardPushPx, ukemiKind, ukemiOn, ukemiParryPushPx } from './ukemi'
import { airWaveOverlapsPlayer, airWaveRadiusPx } from './roar_air_geometry'
import { dodgeFloorPass, dodgeFloorSpendsOnIFrame } from './dodge_floor_rule'
import { finisherImpactSfx, grabStartSfx } from './finisher_sound'

let nextHitId = 1

// 2026-09-03: ジャストパリィで返した飛翔体の対ボス値。近接のジャストパリィと同格＋α。
// 正本 = docs/candidates/2026-09-03-executioner-combat-numbers-fable5-design.md §4
export const REFLECTED_PROJECTILE_BOSS_HIT_DRAFT = {
  damage: 6,
  postureDamage: 30,
} as const

// 連続弾きチェーン (P4 §6.1): 直近パリィから windowMs 以内なら継続 (+1, cap 上限)、超過なら新しい連=1。
export function nextParryChain(
  prev: number, lastParryTime: number | null, now: number,
  windowMs: number, cap: number,
): number {
  if (lastParryTime != null && (now - lastParryTime) * 1000 <= windowMs) {
    return Math.min(cap, prev + 1)
  }
  return 1
}

export type HitSpawnContext = {
  bossX: number
  bossFacing: 'left' | 'right'
  playerX: number
  // 実効の溜め時間 (2026-09-03 第4便・譜面の chargeOverrideMs 対策)。指定時、hazardSpawns の
  //   telegraphMs を「本来の chargeMs との差」だけ補正する (短縮拍で床ハザードだけ据え置き時刻に
  //   なるズレを直す)。未指定=従来どおり telegraphMs をそのまま使う。
  chargeMs?: number
}

// パリィ演出の強弱 = 弾いたヒットの重さ (体幹ダメージ基準 + 連撃なら増し)。旧 combat.ts の式を継承。
function parryIntensityOf(postureDamage: number, comboHits?: number): number {
  return Math.max(0.25, Math.min(1,
    (postureDamage - 20) / 35 + (comboHits && comboHits >= 2 ? 0.15 : 0)))
}

// 弾いて押される向き (DEV ?roarParry=1 の帯だけ・src/exec_roar_wave.ts)。
//   roomy = 主人公の位置から左右の壁までの余白が大きい方 (同じなら away と同じ)。away = ボスから離れる向き。
export function parryKnockSide(
  toward: 'roomy' | 'away', playerX: number, bossX: number, walls: { leftWall: number; rightWall: number },
): 1 | -1 {
  const away: 1 | -1 = playerX >= bossX ? 1 : -1
  if (toward === 'away') return away
  const left = playerX - walls.leftWall
  const right = walls.rightWall - playerX
  return right > left ? 1 : left > right ? -1 : away
}

// hazardWallClearPx で切った帯がこれより細くなったら、その帯は出さない (立てない細さの帯を残さない)。
export const HAZARD_WALL_CLEAR_MIN_WIDTH_PX = 20

// 凍結ゾーン: telegraph 突入時に呼ぶ。座標はこの瞬間に固定 (回り込みで前後が裏返らない)。
export function spawnZoneHits(atk: AttackDef, ctx: HitSpawnContext): ActiveHit[] {
  const dir = ctx.bossFacing === 'right' ? 1 : -1
  const walls = arenaWalls()
  // 溜めの頭の飛び退き (2026-09-03 第4便・exec_maulQuake): telegraphBackstepPx 指定時、
  //   anchor:'boss' の中心は「飛び退いた後」の位置で固定する (boss.ts が同じ tick で実座標を動かす)。
  //   飛び退きの止まり位置はボスの体の壁 (bossBodyBoundsFor・介錯は壁の50px手前) = boss.ts keepBossBodyOnScreen と同じ。
  const hopWalls = atk.sourceBossId && BOSS_BODY_WALL_INSET_PX[atk.sourceBossId] ? bossBodyBoundsFor(atk.sourceBossId) : walls
  const hopX = atk.telegraphBackstepPx
    ? Math.max(hopWalls.leftWall, Math.min(hopWalls.rightWall, ctx.bossX - dir * atk.telegraphBackstepPx))
    : ctx.bossX
  const groupId = atk.hazardHitOncePerCast ? nextHitId : undefined
  // 弾いたら同じ回の残りが当たらない帯 (DEV ?roarParry=1・src/exec_roar_wave.ts) の印。未指定の技には付かない。
  const parryCastId = nextHitId
  // 壁ぎわに逃げ場を残す (2026-10-09 比較版・DEV ?maulQuakeWall=b のときだけ値が入る)。未指定 = 今まで通り。
  const clearPx = atk.hazardWallClearPx
  const clearWalls = clearPx !== undefined ? arenaBoundsFor(atk.sourceBossId) : undefined
  const spawns = (atk.hazardSpawns ?? []).filter((s) => {
    if (!clearWalls || clearPx === undefined) return true
    const c = s.anchor === 'arena' ? arenaAnchorX(s.offset)
      : (s.anchor === 'player' ? ctx.playerX : hopX) + dir * s.offset
    const a = Math.max(clearWalls.leftWall + clearPx, c - s.width / 2)
    const b = Math.min(clearWalls.rightWall - clearPx, c + s.width / 2)
    return b - a >= HAZARD_WALL_CLEAR_MIN_WIDTH_PX
  })
  return spawns.map((s) => {
    const center = s.anchor === 'arena' ? arenaAnchorX(s.offset)
      : (s.anchor === 'player' ? ctx.playerX : hopX) + dir * s.offset
    // 壁の少し外まで許容 (画面端に立って安全、を完全には許さない)
    let xStart = Math.max(walls.leftWall - 40, center - s.width / 2)
    let xEnd = Math.min(walls.rightWall + 40, center + s.width / 2)
    if (clearWalls && clearPx !== undefined) {
      xStart = Math.max(xStart, clearWalls.leftWall + clearPx)
      xEnd = Math.min(xEnd, clearWalls.rightWall - clearPx)
    }
    // 譜面の chargeOverrideMs 対策 (2026-09-03 第4便): 実効の溜め時間が本来の chargeMs と違う時、
    //   telegraphMs をその差だけ補正する (短縮拍で床ハザードの時刻だけ据え置きになるズレを防ぐ)。
    const telegraphMs = ctx.chargeMs !== undefined
      ? Math.max(0, s.telegraphMs + (ctx.chargeMs - atk.chargeMs))
      : s.telegraphMs
    return {
      id: nextHitId++, attackId: atk.id, kind: s.kind,
      xStart, xEnd, elapsedMs: 0,
      telegraphMs, activeMs: s.activeMs,
      damage: s.damage, postureDamage: s.postureDamage,
      parryable: s.parryable === true,   // 床/設置は刃で受けられない (DEV ?roarParry=1 の帯だけ例外)
      hitOnce: s.hitOnce, rehitIntervalMs: s.rehitIntervalMs,
      ...(groupId !== undefined ? { groupId } : {}),
      consumed: false, playerWasHit: false, counterAwarded: false,
      parryIntensity: s.parryable ? parryIntensityOf(s.postureDamage) : 0,
      originX: ctx.bossX, originDir: dir,
      ...(s.parryable && s.parryKnockbackPx ? { parryKnockbackPx: s.parryKnockbackPx } : null),
      ...(s.parryable && s.parryKnockbackToward ? { parryKnockbackToward: s.parryKnockbackToward } : null),
      ...(s.parryable && s.parryEndsCast ? { parryCastId } : null),
      ...(s.ringRadiusPx !== undefined ? { ringRadiusPx: s.ringRadiusPx } : null),
      // 空気の衝撃波 (既定・旧は DEV ?roarAir=0・src/exec_roar_air.ts): 帯ではなく中心から広がる前縁。出る前は幅0 (中心の1点)。
      ...(s.airWave ? { airWave: { ...s.airWave, originX: center }, xStart: center, xEnd: center } : null),
    }
  })
}

// hazardHitOncePerCast の技で、いま場にある「この回」の束の id (= 最新の束)。無ければ undefined。
//   本体の一撃を同じ回の床の帯と束ねるために game.ts が使う (2026-10-02「地割り1回で2発」)。
export function latestCastGroup(hits: readonly ActiveHit[], attackId: string): number | undefined {
  let id: number | undefined
  for (const h of hits) {
    if (h.attackId === attackId && h.groupId !== undefined && (id === undefined || h.groupId > id)) id = h.groupId
  }
  return id
}

// melee (ボス追従) ヒット: active 突入時 (連撃は各段の突入時) に呼ぶ。
//   range 0 (判定の本体がゾーンの攻撃) は melee ヒットを出さない。
//   contact 同期 (契約2026-07-16 §B): pose timeline の contact 進捗を activeMs へ焼き込み、
//   「刃/牙が実際に届く瞬間」まで物理判定を遅らせる。 進捗解決は boss_pose_timeline.ts 側の
//   純関数 (resolveContactProgress) に一元化し、 攻撃ID別の値をここへ直書きしない。
export function spawnMeleeHit(atk: AttackDef, comboHitIndex: number): ActiveHit | null {
  const stage = atk.comboStages?.[comboHitIndex]
  const range = stage?.range ?? atk.range
  if (range <= 0) return null
  let strike = stage?.strike ?? atk.strike
  if (!strike) {
    if (!atk.collisionDraft) {
      throw new Error(`[strike] missing authored profile: ${atk.id} hitIndex=${comboHitIndex}`)
    }
    // 後半ボスの移行中だけ許す明示的legacy形状。無印の欠落は決して黙認しない。
    strike = { localFromPx: -Math.min(range, BOSS.rearHitRange), localToPx: range }
  }
  const biteReach = stanceBiteReach(atk.id)
  if (biteReach !== undefined) strike = { ...strike, localToPx: biteReach }
  // 覚醒王の噛み: 当たりは口の周り (＋暴噛・溜め・連噛・追い噛みの2発目は体の前1/3) (overlapsPlayer が毎tick 絵の口から出す)。ここの strike は控え。
  const mouth = isAwokenMouthBiteHit(atk, comboHitIndex)
  // 覚醒王の致命の壁ぎわの近い形 (10-14・src/awoken_devour_pounce.ts closeQuarters): 口の周りに体の前1/3 を足す (普段の噛みと同じ)。
  const mouthFrontBody = mouth && !!atk.devour?.pounce?.closeQuarters
  if (mouth) strike = awokenBiteStrike(atk.id, awokenMouthBiteFallbackForwardPx(atk.id), mouthFrontBody)
  // DEV の比較版 (src/exec_awoken_reach.ts): 絵の前足が当たりより前に出る段だけ、当たりの前端を絵＋15 まで伸ばす。
  const mouthFrontToPx = mouth ? awokenMouthArtFrontPx(atk.id, comboHitIndex) : undefined
  if (mouthFrontToPx !== undefined) strike = withMouthFront(strike, mouthFrontToPx)
  const postureDamage = stage?.postureDamage ?? atk.postureDamage
  const contactProgress = resolveContactProgress(atk, comboHitIndex)
  const parryable = stage?.parryable ?? atk.parryable
  const contactDelayMs = atk.meleeLiveWindowMs?.[0] ?? atk.activeMs * contactProgress
  // 弾けない赤のその場の当たりは「届いた瞬間から回避の無敵の長さまで」(10-07「赤い攻撃の当たる瞬間を短くする」)。
  // 絵と技の長さは変えず、無敵が旧 200ms より短い時だけ当たりの寿命を詰める (10-07〜10-14 は既定 100ms で詰まっていた。10-14 に既定 200ms へ戻したので、いまは DEV ?dodgeIFrame=b|c の時だけ詰まる)。
  // 同日ユーザー「案1 走り終わりまで当たる」: 覚醒王の走る赤は明示フラグで例外にする。
  // ボス追従の当たりは最初の接触（無敵も含む）で消費済みになるため、通過の全時間を無敵で覆う必要はない。
  const liveEndMs = atk.meleeLiveWindowMs?.[1] ?? atk.activeMs
  const shortened = stepIFrameMs() < PLAYER.stepIFrameMs
  const keepHitWhileTravelling = stage?.keepHitWhileTravelling ?? atk.keepHitWhileTravelling
  const activeMs = parryable === false && shortened && !keepHitWhileTravelling
    ? Math.min(liveEndMs, contactDelayMs + stepIFrameMs()) : liveEndMs
  return {
    id: nextHitId++, attackId: atk.id, kind: 'melee',
    follow: 'boss', range, strike,
    requiresForwardCenter: atk.requiresForwardCenter,
    xStart: 0, xEnd: 0, elapsedMs: 0,
    telegraphMs: 0, activeMs,
    contactDelayMs,
    damage: stage?.damage ?? atk.damage, postureDamage,
    parryable,
    playerLethal: atk.playerLethal,
    bossFinisher: atk.bossFinisher,
    // per-stage 押し出し優先 (処刑人 B5 段0 = ノーダメ突き飛ばし。 他段は knockback を持たない)。
    knockbackPx: stage?.knockbackPx ?? atk.knockbackPx,
    knockbackParryable: atk.knockbackParryable,
    hitKnockbackPx: atk.hitKnockbackPx,
    parryKnockbackPx: atk.parryKnockbackPx,
    // 掴み (処刑人 D8/D9・剣舞の引き寄せは段0だけ): 段の上書きが先。
    grab: stage?.grab ?? atk.grab,
    hitOnce: true,
    consumed: false, playerWasHit: false, counterAwarded: false,
    parryIntensity: parryIntensityOf(postureDamage, atk.comboHits),
    ...(mouth ? { mouth: true as const } : null),
    ...(mouthFrontBody ? { mouthFrontBody: true as const } : null),
    ...(mouthFrontToPx !== undefined ? { mouthFrontToPx } : null),
  }
}

// active 突入時（連撃は各段）に呼ぶ。発射後はボスの向き・位置へ追従しない。
export function spawnCastProjectile(
  atk: AttackDef,
  comboHitIndex: number,
  boss: Boss,
  spawnPoint: { x: number; y: number },
): ActiveHit | null {
  const projectile = atk.castProjectile
  if (!projectile) return null
  const stage = atk.comboStages?.[comboHitIndex]
  const postureDamage = stage?.postureDamage ?? atk.postureDamage
  const dir = boss.facing === 'right' ? 1 : -1
  const motion = resolveBossTimelinePose(boss.id, boss)?.motion
  // 弾も体と同じ timeline offset だけを受ける。scale / rot の足元ピボット二次項は扱わない。
  const spawnX = spawnPoint.x + dir * ((projectile.spawnForwardPx ?? 0) + (motion?.offsetX ?? 0))
  const spawnY = spawnPoint.y + (motion?.offsetY ?? 0)
  // 弾の進行方向の下向きの角度 (度)。未指定=水平。y は見た目だけで当たりは x のみ
  const vy = projectile.descentDeg
    ? Math.tan(projectile.descentDeg * Math.PI / 180) * projectile.speedPxPerSec
    : undefined
  return attachCannonTracking({
    id: nextHitId++, attackId: atk.id, kind: 'melee', follow: 'travel',
    x: spawnX, y: spawnY, vx: dir * projectile.speedPxPerSec, vy, widthPx: projectile.widthPx,
    launchDelayMs: projectile.launchDelayMs,
    xStart: spawnX - projectile.widthPx / 2,
    xEnd: spawnX + projectile.widthPx / 2,
    elapsedMs: 0, telegraphMs: 0, activeMs: atk.activeMs,
    damage: stage?.damage ?? atk.damage,
    postureDamage,
    parryable: stage?.parryable ?? atk.parryable,
    hitOnce: true,
    consumed: false, playerWasHit: false, counterAwarded: false,
    parryIntensity: parryIntensityOf(postureDamage, atk.comboHits),
  })
}

export type HitsOutcome = {
  player: Player
  boss: Boss
  hits: ActiveHit[]
  sfx: SfxId[]
  fx: FxEvent[]
  hitStopMs: number
  shake: ScreenShake | null
  counterOpened: boolean
  // 死因表示 (P4): この tick の被弾でプレイヤーが HP0 に落ちたとき、 その致命ヒットの attackId。
  killedByAttackId?: string
}

// 覚醒王の噛みの口の中心 (いま描いている絵とタイムラインの変位から)。絵が口の表に無ければ undefined。
function awokenMouthNow(boss: Boss): AwokenMouthPoint | undefined {
  const pose = resolveBossTimelinePose(boss.id, boss)
  return awokenMouthPoint(pose?.frame?.current.image, pose?.motion ?? NO_POSE_MOTION, boss.altitudePx ?? 0)
}

// 比較版の前端 (ActiveHit.mouthFrontToPx) まで当たりの前を伸ばす。値が無ければそのまま。
function withMouthFront(strike: StrikeProfile, frontToPx: number | undefined): StrikeProfile {
  return frontToPx === undefined ? strike : { ...strike, localToPx: Math.max(strike.localToPx, frontToPx) }
}

// ボス追従の近接ヒットの「いまの」横の区間。実判定 (overlapsPlayer) と dev H 表示 (render.ts) が共有する。
export function meleeStrikeNow(hit: ActiveHit, boss: Boss): StrikeProfile | undefined {
  if (hit.mouth) {
    const mouth = awokenMouthNow(boss)
    if (mouth) return withMouthFront(awokenBiteStrike(hit.attackId, mouth.forwardPx, hit.mouthFrontBody), hit.mouthFrontToPx)
  }
  // 騎士の固有の致命の前の版「背向けの一閃」(DEV ?execUnique=1&execUniqueKnight=band・src/exec_unique.ts): 当たりの前端は光の帯の前端 + 15 と一緒に伸びる。
  //   既定の突進 (charge・10-12) は伸ばさない (K3 の穂先 + 15 の固定・穂先が主人公に届いたコマで当たる)。
  const uplan = boss.currentAttack?.uniqueExec
  if (hit.attackId === 'knightExecution' && hit.strike && uplan?.boss === 'vigil-knight' && uplan.form === 'band') {
    return { ...hit.strike, localToPx: Math.min(hit.strike.localToPx, knightStrikeFrontPx(hit.elapsedMs)) }
  }
  // 騎士の切り下がり (10-02): 当たりは光の先と一緒に前へ伸びる (光がまだ来ていない所では当たらない・src/knight_cross_arc.ts)。
  if (hit.attackId === 'lanceCross' && hit.strike) {
    const front = knightCrossStrikeFrontPx(hit.elapsedMs, hit.strike.localToPx)
    if (front < hit.strike.localFromPx) return undefined
    return { ...hit.strike, localToPx: front }
  }
  return hit.strike
}

function overlapsPlayer(p: Player, hit: ActiveHit, boss: Boss): boolean {
  if (hit.follow === 'boss') {
    if (isWallHuntGame(boss)) return wallHuntOverlapsPlayer(boss, p)
    if (!hit.strike) throw new Error(`[strike] active melee has no profile: ${hit.attackId}`)
    if (hit.requiresForwardCenter) {
      const dir = boss.facing === 'right' ? 1 : -1
      if ((p.x - boss.x) * dir < 0) return false
    }
    // 覚醒王の噛み (2026-09-26): 口の周りが当たる。口が主人公の高さに無い (空中の口が頭の上を過ぎる) 間は口では当たらない。
    //   2026-09-27: 暴噛・溜め・暴連噛・伸び・追い噛みの2発目は体の前1/3 (高さ全部) でも当たる。それより後ろ (懐の奥・胴の後ろ) は無害。
    if (hit.mouth) {
      const mouth = awokenMouthNow(boss)
      if (mouth) {
        const overlaps = (strike: StrikeProfile) => playerHurtboxOverlapsStrike(p.x, PLAYER.width,
          strikeWorldInterval(boss.x, boss.facing, strike))
        if ((awokenBiteUsesFrontBody(hit.attackId) || hit.mouthFrontBody)
          && awokenFrontBodyReachesHeight(boss.altitudePx ?? 0, PLAYER_DISPLAY_HEIGHT)
          && overlaps(withMouthFront(AWOKEN_BITE_FRONT_THIRD, hit.mouthFrontToPx))) return true
        if (!awokenMouthReachesHeight(mouth, PLAYER_DISPLAY_HEIGHT)) return false
        // 致命 (口の周りだけの噛み) の比較版は、口の区間の前を絵＋15 まで伸ばす。前1/3 を持つ噛みは上で伸ばし済み。
        return overlaps(awokenBiteUsesFrontBody(hit.attackId) ? awokenMouthStrike(mouth.forwardPx)
          : withMouthFront(awokenMouthStrike(mouth.forwardPx), hit.mouthFrontToPx))
      }
    }
    // 高さを見る噛みつき (大暴れ第5案): 空中の口は真下の主人公に届かない。band 未指定は従来どおり横だけ。
    if (!bandOverlapsPlayer(hit, boss, PLAYER_DISPLAY_HEIGHT)) return false
    const strike = meleeStrikeNow(hit, boss)
    if (!strike) return false
    const interval = strikeWorldInterval(hit.anchorX ?? boss.x, hit.anchorFacing ?? boss.facing, strike)
    return playerHurtboxOverlapsStrike(p.x, PLAYER.width, interval)
  }
  if (hit.follow === 'travel') {
    if (!cannonProjectileOverlapsHeight(hit, p.y, PLAYER_DISPLAY_HEIGHT)) return false
    if (hit.spirit?.spent) return false
    if (!spiritOverlapsHeight(hit, p.y, PLAYER_DISPLAY_HEIGHT)) return false
    if (hit.x === undefined || hit.widthPx === undefined) {
      throw new Error(`[travel] active projectile has no x/width: ${hit.attackId}`)
    }
    const half = hit.widthPx / 2
    const playerHalf = PLAYER.width / 2
    return p.x + playerHalf >= hit.x - half && p.x - playerHalf <= hit.x + half
  }
  // 空気の衝撃波 (既定): 左右の前縁 (厚み) が体に重なった時だけ。帯の内側 (前縁の後ろ) は当たらない。
  if (hit.airWave) return airWaveOverlapsPlayer({ elapsedMs: hit.elapsedMs, telegraphMs: hit.telegraphMs, airWave: hit.airWave }, p.x)
  const half = PLAYER.width / 2
  return p.x + half >= hit.xStart && p.x - half <= hit.xEnd
}

function reflectedProjectileOverlapsBoss(hit: ActiveHit, boss: Boss): boolean {
  if (hit.follow !== 'travel' || !hit.reflected) return false
  if (!cannonProjectileOverlapsHeight(hit, boss.y, BOSS.height)) return false
  // 返した怨念は空中のボスにも当たる (高さ込み)。
  if (!spiritOverlapsHeight(hit, boss.y - (boss.altitudePx ?? 0), BOSS.height)) return false
  if (hit.x === undefined || hit.widthPx === undefined) {
    throw new Error(`[travel] reflected projectile has no x/width: ${hit.attackId}`)
  }
  const half = hit.widthPx / 2
  const bossHalf = boss.bodyHalfWidthPx
  return boss.x + bossHalf >= hit.x - half && boss.x - bossHalf <= hit.x + half
}

// melee/zone 共通の「今 live か」判定 (契約2026-07-16 §B)。 実判定 (updateHits) と dev debug 表示
//   (render.ts drawHitboxesDev) が同じ関数を使う = 見た目 (contact マーカー) と当たり判定のズレを断つ。
//   - melee (follow==='boss'): pose と同じ boss.stateElapsed*1000 を時計にする。 hit.elapsedMs は
//     spawn 同tickで +dt される (最大1 fixed tick 先行) ため contact 時計には使わない。
//   - zone (床ハザード): 従来どおり hit の自己時計 (elapsedMs/telegraphMs/activeMs) を使う。挙動不変。
export function isHitLive(hit: ActiveHit, boss: Boss): boolean {
  if (hit.follow === 'boss') {
    if (!(boss.state === 'active' && boss.currentAttack?.id === hit.attackId)) return false
    const clockMs = boss.stateElapsed * 1000
    return clockMs >= (hit.contactDelayMs ?? 0) && clockMs < hit.activeMs
  }
  if (hit.follow === 'travel') return hit.elapsedMs >= (hit.launchDelayMs ?? 0)
  return hit.elapsedMs >= hit.telegraphMs && hit.elapsedMs < hit.telegraphMs + hit.activeMs
}

// 実際の衝突処理へ進める状態。dev表示も必ずこれを使い、消費済みhitをlive色で描かない。
/** 体の振り (ボスに付いて動く近接か、生まれた帯で当てる振り hit.swing)。 ガード・回避の反撃・受け身・回避での使い切りはこれで見る。
 *  床の帯・飛び道具は入らない (位置取りで避ける物・11-03 軍配者たちの振りを他のボスの振りとそろえた)。 */
export function isSwingHit(hit: ActiveHit): boolean {
  return hit.follow === 'boss' || (hit.follow === undefined && hit.swing === true)
}

export function isHitCollisionActive(hit: ActiveHit, boss: Boss): boolean {
  return !hit.consumed && isHitLive(hit, boss)
}

function shakeFrom(spec: { amplitude: number; durationMs: number }, time: number): ScreenShake {
  const dur = spec.durationMs / 1000
  return { remainingSec: dur, amplitude: spec.amplitude, startAmplitude: spec.amplitude,
    totalDur: dur, startSec: time }
}

function strongerShake(a: ScreenShake | null, b: ScreenShake): ScreenShake {
  if (!a || b.startAmplitude >= a.startAmplitude) return b
  return a
}

// Adopted 2026-09-24: receiving/dodging the rush does not interrupt its momentum.
// Match the current melee hit, so other simultaneous attacks keep their feedback.
function skipsAwokenRushContactStop(boss: Boss, hit: ActiveHit): boolean {
  return boss.id === 'crowned-king-awoken' && boss.state === 'active'
    && hit.follow === 'boss' && hit.attackId === boss.currentAttack?.id
    && (hit.attackId === 'feralRush' || (hit.attackId === 'feralRushChase' && boss.comboHitIndex === 0))
    && !(import.meta.env.DEV && restoresAwokenRushContactStopForStudy())
}

// 毎tick呼ぶ。全ヒットを老化させ、パリィ → i-frame → 被弾 の順に解決し、寿命切れを除去する。
export function updateHits(
  player: Player, boss: Boss, hits: ActiveHit[], dtMs: number, time: number,
): HitsOutcome {
  let p = player
  let b = boss
  const sfx: SfxId[] = []
  const fx: FxEvent[] = []
  let hitStopMs = 0
  let shake: ScreenShake | null = null
  let counterOpened = false
  let killedByAttackId: string | undefined
  const survivors: ActiveHit[] = []
  // hazardHitOncePerCast の束で、この tick に当たったもの (同じ tick の後ろの帯も止める)。
  const spentGroups = new Set<number>()
  // 弾かれた回 (DEV ?roarParry=1 の帯だけ)。同じ回の残りの帯は消費済みにする (描画は残る・もう当たらない)。
  const parriedCasts = new Set<number>()

  for (const hit of hits) {
    const elapsed = hit.elapsedMs + dtMs
    let next: ActiveHit = { ...hit, elapsedMs: elapsed }
    if (next.parryCastId !== undefined && !next.consumed && parriedCasts.has(next.parryCastId)) {
      next = { ...next, consumed: true }
    }
    if (next.groupId !== undefined && !next.groupSpent && spentGroups.has(next.groupId)) {
      next = { ...next, groupSpent: true }
    }
    if (next.follow === 'travel') {
      if (next.x === undefined || next.vx === undefined || next.widthPx === undefined) {
        throw new Error(`[travel] projectile has no x/vx/width: ${next.attackId}`)
      }
      // launchDelayMs の間は発射点に留まる (2026-09-09・発勁の突き作り直し): x/y を動かさない。
      //   elapsedMs は上ですでに進めてある。
      if (next.elapsedMs < (next.launchDelayMs ?? 0)) {
        // 留まっている間も xStart/xEnd は現在地に合わせておく (isHitLive=false なので当たらないが、
        //   描画 (drawCastProjectiles) は hit.x/hit.y をそのまま読むため既に正しい)。
        // 大暴れの怨念だけは hoverSpirit を通す (既定は生まれた点に留まり頭の向きだけ変える／DEV の follow は席へ漂う・当たりは無い・他の飛び道具は素通し)。
        next = hoverSpirit(next, p)
      } else {
        next = steerSpirit(steerCannonProjectile(next, p, dtMs), p, b, dtMs)
        const x = next.x! + next.vx! * (dtMs / 1000)
        const y = next.vy !== undefined ? (next.y ?? 0) + next.vy * (dtMs / 1000) : next.y
        next = recordSpiritTravel(next, x - next.x!, (y ?? 0) - (next.y ?? 0))
        next = { ...next, x, y, xStart: x - next.widthPx! / 2, xEnd: x + next.widthPx! / 2 }
      }
    }
    // 空気の衝撃波 (既定): 左右の前縁の間を xStart/xEnd に写す (dev の H 表示用。当たりは overlapsPlayer が前縁で見る)。
    if (next.airWave) {
      const r = airWaveRadiusPx({ elapsedMs: next.elapsedMs, telegraphMs: next.telegraphMs, airWave: next.airWave })
      next = { ...next, xStart: next.airWave.originX - r, xEnd: next.airWave.originX + r }
    }
    const lifeMs = hit.telegraphMs + hit.activeMs
    const live = isHitCollisionActive(next, b)
    // 回避・見切りと床の帯 (src/dodge_floor_rule.ts)。配布版は地割りの帯だけ反撃の突きの間は当たらない・DEV ?dodgeFloor=now|counter|spend。
    const floorPass = live ? dodgeFloorPass(next, p, () => !next.reflected && overlapsPlayer(p, next, b)) : { skip: false, hit: next }
    next = floorPass.hit

    if (live && reflectedProjectileOverlapsBoss(next, b)) {
      // 通常攻撃と同じ被弾ガードを鏡写しにする。致命直後の一拍／blink中に届いた玉は
      // ダメージや体幹を与えず、その場で消える。staggered はここで除外しない。
      if (b.postExecuteInvincMs > 0 || b.state === 'blink') {
        next = { ...next, consumed: true }
        continue
      }
      const reflected = REFLECTED_PROJECTILE_BOSS_HIT_DRAFT
      const hp = Math.max(chipFloor(b), b.hp - reflected.damage)
      const posture = Math.min(b.maxPosture, b.posture + reflected.postureDamage * bossPostureGainMul(b))
      b = {
        ...b,
        hp,
        posture,
        postureRegenLockSec: reflected.postureDamage > 0
          ? Math.max(b.postureRegenLockSec ?? 0, bossPostureGraceSec(b.id))
          : (b.postureRegenLockSec ?? 0),
        recentHitAgesMs: [...b.recentHitAgesMs, 0],
      }
      next = { ...next, consumed: true }
      sfx.push('hit_boss')
      fx.push({ kind: 'hit_spark', x: next.x ?? b.x, y: next.y ?? b.y - BOSS.height / 2, t: time, color: 'pink' })
      // 弾き返した怨念が王に当たった: 黄緑の芯が弾ける (09-21 総評・手元のパリィ光に比べて王側の反応が控えめだった)。
      if (next.spirit) fx.push({ kind: 'rampage_contact', variant: 'orb_impact', x: next.x ?? b.x, y: next.y ?? b.y - BOSS.height / 2, t: time, dir: 1 })
      hitStopMs = Math.max(hitStopMs, COMBAT.playerAttack.hitStopMs)
      shake = strongerShake(shake, shakeFrom({ amplitude: 1.5, durationMs: 75 }, time))
      if (b.state !== 'staggered' && (b.posture >= b.maxPosture || b.hp <= chipFloor(b))) {
        b = staggerBoss(b)
        sfx.push('stagger')
        shake = strongerShake(shake, shakeFrom(COMBAT.stagger.shake, time))
      }
    } else if (!next.reflected && live && !floorPass.skip && overlapsPlayer(p, next, b)) {
      // 致命3連中・掴み拘束中は完全無敵 (攻撃・床技・ノックバック含め全スキップ)。
      //   execute アニメ中に既存攻撃・持続ハザードが当たる問題を防ぐ (2026-06-25)。
      //   grabbed (処刑人 D8/D9) も同様: 拘束中に他の生きたヒットへ二重被弾しない。
      if (p.state === 'executing' || p.state === 'grabbed') {
        survivors.push(next)
        continue
      }
      // 致命を弾ける仕組み (既定・旧は DEV ?execParry=off・src/exec_parry.ts): 崩れている間は致命だけが当たる
      //   (連撃の残り・床・飛び道具は素通り＝崩れた主人公に普通の技が入る症状 K2・C5 を消す)。
      const execStun = execStunActive(p, b.id)
      const isFinisherHit = !!(next.bossFinisher || next.playerLethal) && next.follow === 'boss'
      if (execStun && !isFinisherHit) {
        survivors.push(next)
        continue
      }
      if (execStun && isFinisherHit && execParryAccepts(stunParryWindowAt(p, time * 1000))) {
        // 剣舞の手拍子の致命 (src/blade_finisher_v2.ts・既定・前の版は DEV ?bladeFinisher=v1) の1撃目: 崩れたまま受け止める (ボスの体幹も押し戻しも無し)。
        //   2撃目 (1拍後) も弾いて初めて「致命を弾いた」になる。押し直せるように、受け止めた押しを印に残す (exec_parry.ts stunParryAccepts)。
        if (b.currentAttack?.bladeClap && b.comboHitIndex < (b.currentAttack.comboHits ?? 1) - 1) {
          p = { ...p, stunParryCaughtMs: p.stunParryPressMs }
          next = { ...next, consumed: true }
          sfx.push('parry')
          const dir1 = p.facing === 'right' ? 1 : -1
          fx.push({ kind: 'parry_spark', x: p.x + dir1 * 22, y: p.y - 110, t: time, intensity: EXEC_PARRY.sparkIntensity * 0.7, isJust: false })
          hitStopMs = Math.max(hitStopMs, BLADE_FINISHER_CATCH_STOP_MS)
          survivors.push(next)
          continue
        }
        // --- 致命を弾いた: ダメージ0・押されて後ずさる・ボスはのけぞる (sfx 'parry' → 描画の recoil)・ボスの体幹は普段の弾きの3回ぶん (崩れる1歩手前まで) ---
        const isJust = stunParryWindowAt(p, time * 1000) === 'just'
        const pressMs = p.stunParryPressMs!
        playlogRecv(isJust ? 'just' : 'parry', next, p, b, time, { vsDeathblow: true })  // 試遊の記録 (見るだけ・致命を弾いた印)
        const finalRoom = b.executesTaken >= Math.max(1, b.roomCount) - 1
        const postureMul = (b.currentString?.parryPostureMul ?? 1) * (finalRoom ? (b.finalRoomPostureMult ?? 1) : 1)
        const bossGain = isJust ? COMBAT.parrySuccess.bossPostureGain : COMBAT.parrySuccess.normalParryBossPostureGain
        // 既定 (10-12 K-5「どんでん返し」): 普段の弾き1回 (連続の段を除く・幕の体力の倍率も普段と同じ) の3倍・崩れる1歩手前まで (src/exec_parry.ts)。
        //   旧 (DEV ?execParryGain=old) = 普段の弾き1回と同じ量 (幕の体力の倍率なし・満タンなら下で崩れる)。
        const oneParryGain = (parryOneGains()?.boss ?? bossGain) * postureMul * parryGainHpMul(b) * bossPostureGainMul(b)
        // D 命拾いだけ (src/exec_bait.ts・既定 fix): ガードで崩れた時は普段の弾き1回ぶん・崩れる手前で止める。
        const execPosture = lifelineApplies(p)
          ? lifelineBossPosture(b.posture, b.maxPosture, oneParryGain)
          : execParryBigGainOn()
          ? execParryBossPostureAfter(b.posture, b.maxPosture, oneParryGain)
          : Math.min(b.maxPosture, b.posture + bossGain * postureMul * bossPostureGainMul(b))
        // C つづきから (src/exec_bait.ts・既定 fix): 崩した連を次の待機で頭からやり直す。
        b = armEncore(b)
        b = { ...b, posture: execPosture,
          postureRegenLockSec: Math.max(b.postureRegenLockSec ?? 0, bossPostureGraceSec(b.id)) }
        // 騎士と花嫁の固有の致命 (DEV ?execUnique=1・src/exec_unique.ts): 弾かれた印を段取りに立てる (絵の並びが「弾かれた姿」へ替わる・花嫁は覆い被さらない)。
        const uplan = b.currentAttack?.uniqueExec
        if (uplan && !uplan.parried) b = { ...b, currentAttack: { ...b.currentAttack!, uniqueExec: { ...uplan, parried: true } } }
        // 弾かれた印 (全ボス・src/exec_parry_recoil.ts): 描画が弾いたコマから弾かれの姿へ替える。
        if (b.currentAttack && !b.currentAttack.execParried) b = { ...b, currentAttack: { ...b.currentAttack, execParried: true } }
        const away = p.x >= b.x ? 1 : -1
        // 崩れは解け、受けた姿 (parrying) のまま押される。体幹は崩れ明けと同じ 0。経過は押した時からの続き (アニメが途切れない)。
        //   受付の長さは普段のパリィと同じ (stunParryCatchMs = 120ms)・縮めた分は普段どおり足し分 (parryCycleTailMs) で1回の長さを保つ。
        p = { ...p, state: 'parrying', stateElapsed: time - pressMs / 1000,
          parryAbsorbed: true, parryActiveMsEffective: stunParryCatchMs(),
          ...(parryOneOn() ? { parryCycleTailMs: parryOneTailMs(playerParryActiveMs()) } : null),
          lastParryPressMs: pressMs, parryMashCount: 0,
          attackHitConsumed: false, attackComboOpenUntilMs: -9999, stunParryPressMs: undefined,
          posture: 0, parryChain: 1, lastParryTime: time,
          postureRegenLockSec: Math.max(p.postureRegenLockSec, PLAYER.perfectParryRegenLockSec),
          knockbackRemainingPx: 0, knockbackConsumePerTick: undefined, execPush: undefined }
        // 押し戻しの版 (DEV ?execPush=old|new|sekiro・src/exec_parry.ts)。old = 前の版 (残りの 17% ずつ減る・止め 210ms・乱れた揺れ)。
        //   new / sekiro = 決めた進み方で滑る (止め明けから)・止めは短い・揺れは止め明けに押される向きへ (描画)。
        const pushVer = execPushVersion()
        if (pushVer === 'old') {
          p = { ...p, knockbackRemainingPx: away * execParryPushPx(b.id), knockbackConsumePerTick: EXEC_PARRY.pushConsumePerTick,
            execPushHoldUntilMs: time * 1000 + execParrySlideMs(execParryPushPx(b.id)) + EXEC_PARRY_SETTLE_MS }
        } else {
          const plan = planExecPush(b.id, p.x, away, arenaBoundsFor(b.id), pressMs, pushVer)
          p = { ...p, execPush: plan, execPushHoldUntilMs: time * 1000 + plan.durationMs + EXEC_PUSH[pushVer].holdAfterMs }
        }
        next = { ...next, consumed: true }
        const bladeDir = p.facing === 'right' ? 1 : -1
        sfx.push('parry', 'guard')
        // 騎士の固有の致命 (DEV ?execUnique=1): 光の帯は腰の高さを通るので、弾いた光も帯の高さに出す (それ以外は今のまま胸の高さ)。
        const sparkY = b.currentAttack?.uniqueExec?.boss === 'vigil-knight' ? p.y - KNIGHT_EXEC_BLADE_HEIGHT_PX : p.y - 110
        fx.push({ kind: 'parry_spark', x: p.x + bladeDir * 22, y: sparkY, t: time, intensity: EXEC_PARRY.sparkIntensity, isJust, exec: true })
        if (pushVer === 'old') {
          // 足元の砂: 押される向きへ低く舞う (騎士の踏み込みの砂を流用・描くだけ。dir の逆へ舞う絵なので -away を渡す)。
          fx.push({ kind: 'knight_lunge_dust', variant: 'kick', x: p.x, y: p.y, t: time, dir: -away, amount: 0.8 })
          hitStopMs = Math.max(hitStopMs, EXEC_PARRY.successHitStopMs)
          shake = strongerShake(shake, shakeFrom(EXEC_PARRY.successShake, time))
        } else {
          const spec = EXEC_PUSH[pushVer]
          // 押し戻しの重さ (既定 c ボスの重さで段・DEV ?execPushWeight=off|a|b・new の時だけ・src/exec_push_weight.ts): 止めの長さを版で替え、
          //   カメラの寄り・フラッシュ・揺れは光の長い残りの印 (pw) を見て描画が出す。off は重ねない (new の止め・揺れのまま)。
          const pwVar = pushVer === 'new' ? pushWeightVariant() : 'off'
          const pwSpec = pwVar === 'off' ? null : pushWeightSpec(pwVar, b.id)
          const pw = pwVar === 'off' ? undefined : { v: pwVar, bossId: b.id, bossX: b.x, playerX: p.x, playerY: p.y }
          // 光の長い残り (と火の粉)。弾いた光 (parry_spark) の上に重ねる。
          fx.push({ kind: 'exec_parry_glow', x: p.x + bladeDir * 22, y: sparkY, t: time, isJust, embers: spec.feet === 'embers', dir: away, ...(pw ? { pw } : null) })
          if (spec.impactDustAmount > 0) {
            fx.push({ kind: 'knight_lunge_dust', variant: 'kick', x: p.x, y: p.y, t: time, dir: -away, amount: spec.impactDustAmount })
          }
          // 1014c (既定): 止めは全ボス同じ (src/exec_parry_slow_fx.ts の EXEC_SLOW_STOP_MS・A-6「差を付けなくてよい」)。b1014 / old は段のまま。
          hitStopMs = Math.max(hitStopMs, pwSpec ? execSlowStopMs(pwSpec.hitStopMs) : spec.hitStopMs)
        }
        if (isJust && b.posture >= b.maxPosture && b.state !== 'staggered') {
          b = staggerBoss(b)
          sfx.push('stagger')
          shake = strongerShake(shake, shakeFrom(COMBAT.stagger.shake, time))
        }
      } else if (next.knockbackPx && !(next.knockbackParryable && next.parryable && isPlayerParryActive(p))) {
        // ノックバック: 回避・パリィ・ダメージより前に無条件適用 (設計書 2026-06-16 §4-3)。
        //   例外 = knockbackParryable (騎士の突き飛ばし 2026-10-01): 弾けば下のパリィ成功へ進み、押されない。
        //   ★ i-frame 中でも・パリィ中でも押し出される (避ける対象でない)。
        //   ★ ダメージ・体幹・カウンター窓・パリィ吸収は一切やらない (無害な再配置)。
        const away = p.x >= b.x ? 1 : -1
        p = { ...p, knockbackRemainingPx: away * next.knockbackPx }
        // 騎士の柄の押し返し (knockbackParryable・10-01): 立ち絵のまま滑らないよう、のけぞり (被弾と同じ姿・ダメージなし) を出す。
        //   音は仮に 'guard' (鎧が受け止める鈍い音)。介錯の押し出しは従来どおり (姿も音も出さない)。
        if (next.knockbackParryable) {
          p = { ...p, hurtVisualUntil: time + 0.4, hurtNonce: p.hurtNonce + 1 }
          sfx.push('guard')
          shake = strongerShake(shake, shakeFrom({ amplitude: 2, durationMs: 90 }, time))
        }
        next = { ...next, consumed: true }
        playlogRecv('knockback', next, p, b, time)  // 試遊の記録 (見るだけ)
      } else if (next.parryable && isPlayerParryActive(p)) {
        // --- パリィ成功: このヒットだけを消費 (連撃の次の段は残る) ---
        // ジャスト判定: パリィ開始から perfectParryWindowMs 以内に吸収したか
        //   パリィは1種類 (既定・2026-10-10 ユーザー裁定): 受付 (120ms) に入った弾きは全部ジャスト扱い (普通のパリィは無い・src/parry_one.ts)。
        //   DEV の ?parryOne=off だけ旧 (80ms 以内がジャスト・以降は普通のパリィ)。
        const isJust = parryOneOn() || p.stateElapsed * 1000 <= PLAYER.perfectParryWindowMs
        const oneGains = parryOneGains()   // DEV ?parryOne=b の時だけ量を替える (既定 a・off は null = 旧のジャストの量)
        // 怨念 (大暴れ第5案) は弾いても自分の体幹が溜まらない＝弾き続けても自分が崩れない (09-19 公平さの直し)。
        const selfGain = next.spirit ? 0
          : oneGains ? oneGains.self
          : isJust ? PLAYER.perfectParrySelfPostureGain
          // 花嫁の裾乱れの比較版 (DEV ?brideFlurry=a|ab) だけ速い連を軽くする。配布版は base のまま (src/bride_flurry_trial.ts)。
          : brideFlurryNormalParrySelfGain(next.attackId, b.comboHitIndex, normalParrySelfGainFor(next.attackId, econ().normalParrySelfPostureGain))
        const bossGain = oneGains ? oneGains.boss : isJust ? COMBAT.parrySuccess.bossPostureGain : COMBAT.parrySuccess.normalParryBossPostureGain
        // 通常パリィで体幹が溢れる場合は stun (ジャストは溢れても踏みとどまる)
        const willStunSelf = !isJust && (p.posture + selfGain) >= playerMaxPosture()
        const selfPosture = Math.min(playerMaxPosture(), p.posture + selfGain)
        // 連ごとの体幹ゲイン倍率 (純パリィ5連の溢れ防止) × 最終部屋倍率 (クライマックスを延ばす)。 未指定=1。
        const finalRoom = b.executesTaken >= Math.max(1, b.roomCount) - 1
        const finalMul = finalRoom ? (b.finalRoomPostureMult ?? 1) : 1
        //   技ごとの倍率 (AttackDef.parryPostureMul・未指定=1) も掛ける (10-24 剣舞の終舞12発)。
        const postureMul = (b.currentString?.parryPostureMul ?? 1) * (b.currentAttack?.parryPostureMul ?? 1) * finalMul
        // 連続弾きエスカレーション (P4 §6.1): 途切れず弾くほど段が上がる。
        const chain = nextParryChain(p.parryChain, p.lastParryTime, time,
          COMBAT.parryChain.windowMs, COMBAT.parryChain.cap)
        playlogRecv(isJust ? 'just' : 'parry', next, p, b, time, { broke: willStunSelf, chain, selfPost: selfGain })  // 試遊の記録 (見るだけ)
        // 🔴 2026-07-29: ここまで chain は **火花と振動にしか使われていなかった**。
        //   SEKIRO は「連続で弾くほど敵の体幹ダメージが大きくなる」(Fextralife/Posture)。
        //   実測で全ボスが HP 削り切りになっていた原因のひとつがこれ＝連撃を弾き切る報酬が
        //   数値になっていなかった。台帳 = docs/decision-ledger.md §体幹経済
        //   ⚠️ 戻し方: COMBAT.parryChain.posturePerStep = 0 にすると従来と完全に同じ挙動へ戻る。
        const chainPostureMul = 1 + (chain - 1) * COMBAT.parryChain.posturePerStep
        // ②本物のSEKIRO化 (2026-07-16): ゲインは幕HP率でスケールしない (gain-wall 撤去・
        //   combat.ts と同じ)。 turtle 対策は回復側 (posture.ts bossPostureRecoveryMult) 一本化。
        // 🔵 2026-09-03 第5便 ユーザー裁定: 飛び道具は弾いた瞬間にはボスの体幹を溜めない。
        //   溜まるのは弾き返した弾がボスへ着弾した時 (上の reflectedProjectileOverlapsBoss 分岐・
        //   REFLECTED_PROJECTILE_BOSS_HIT_DRAFT) だけ。
        const isProjectile = next.follow === 'travel'
        const bossPosture = isProjectile
          ? b.posture
          : Math.min(b.maxPosture, b.posture + bossGain * postureMul * chainPostureMul * parryGainHpMul(b) * bossPostureGainMul(b))
        // ジャスト/通常ともに回復停止タイマーをセット。ジャストは短め、通常は長め。
        // 怨念は回復停止も掛けない (09-20: 0.45秒おきの怨念パリィで回復停止が切れ目なく続き、
        //   噛みつきの通常パリィの体幹が技の間ずっと戻らず積み上がっていた)。
        const regenLock = next.spirit ? p.postureRegenLockSec
          : isJust
          ? Math.max(p.postureRegenLockSec, PLAYER.perfectParryRegenLockSec)
          : Math.max(p.postureRegenLockSec, PLAYER.normalParryRegenLockSec)
        p = { ...p, posture: selfPosture, parryAbsorbed: true,
          parryChain: chain, lastParryTime: time, postureRegenLockSec: regenLock }
        if (willStunSelf && p.state !== 'stunned') { p = stunPlayer(p, 'parry'); b = rememberStunSource(b) }
        // 🔵 2026-09-04 介錯・砲身薙ぎ 'shove' 案: 弾いても押し返される (報酬は上のとおり全部付く。位置だけ動く)。
        //   移動・壁クランプ・減速は updatePlayer の applyKnockbackDrift に任せる (hitKnockbackPx と同じ経路)。
        if (next.parryKnockbackPx) {
          // 咆哮の波の比較版 (DEV ?roarParry=1) は向きを選べる (roomy = 壁までの余白が大きい方)。未指定 = ボスから離れる向き。
          const away = next.parryKnockbackToward
            ? parryKnockSide(next.parryKnockbackToward, p.x, b.x, arenaBoundsFor(b.id))
            : p.x >= b.x ? 1 : -1
          p = { ...p, knockbackRemainingPx: away * next.parryKnockbackPx }
        } else if (ukemiOn() && !willStunSelf && isSwingHit(next)) {
          // 受け身 (10-21・src/ukemi.ts・前の版 DEV ?ukemi=off): 弾いた反動で少し下がる。 重い技ほど大きく (腰を落とし後ろ足に砂ぼこり)。
          const away = p.x >= b.x ? 1 : -1
          p = { ...p, knockbackRemainingPx: away * ukemiParryPushPx(next.parryIntensity) }
          fx.push(...ukemiDustFx(ukemiKind(next.parryIntensity), p.x, p.y, away, time))
        }
        if (next.parryCastId !== undefined) parriedCasts.add(next.parryCastId)
        // パリィ=体幹ゲイン → ボス側の回復停止タイマーをリセット (v2 経済。v1 は 0=無効)
        // 請われぬ介錯の形態はパリィで壊れない。変形は攻撃終了時だけに限定する。
        // isProjectile: 弾いた瞬間は体幹を溜めないので、回復停止タイマーも触らない (据え置き)。
        b = { ...b, posture: bossPosture,
          postureRegenLockSec: isProjectile
            ? (b.postureRegenLockSec ?? 0)
            : Math.max(b.postureRegenLockSec ?? 0, bossPostureGraceSec(b.id)),
          // 走り抜け突進は、弾いた地点を新しい移動起点/終点にしてroot motionだけ停止する。
          // active と recovery は通常尺を保つので、パリィ報酬と攻撃後の隙を両立する。
          ...(b.currentAttack?.stopMovementOnParry
            ? { actionAnchorX: b.x, lungeTargetX: b.x }
            : null),
        }
        // 怨念 (大暴れ第5案・D3) は普通のパリィでも返り、返弾は空中のボスを追う (steerSpirit)。
        next = next.follow === 'travel' && next.spirit
          ? { ...next, consumed: false, reflected: true,
              spirit: { ...next.spirit, reflectedAtMs: next.elapsedMs } }
          : next.follow === 'travel' && isJust
          ? (() => {
              // 🔵 2026-09-03 弾き返した弾はボスの胸へ直線で向かう（飛んできた軌道を逆に戻るのは変・ユーザー裁定）。速さは保つ
              const speed = Math.hypot(next.vx ?? 0, next.vy ?? 0) || 1
              const tx = b.x
              const ty = b.y - BOSS.height
              const dx = tx - (next.x ?? 0)
              const dy = ty - (next.y ?? 0)
              const len = Math.hypot(dx, dy) || 1
              const vx = speed * dx / len
              const vy = speed * dy / len
              return { ...next, consumed: false, reflected: true, vx, vy }
            })()
          : { ...next, consumed: true }
        // 発生源は「掲げた刀身」(足元から前へ+上へ)。 player.y は足元、 facing 方向が前。
        const bladeDir = p.facing === 'right' ? 1 : -1
        // 弾いたのに自分が崩れた (09-22 体幹の票 案A): 成功の光と弾きの音を出さず「押し負けた」を見せる。
        const breakLook = willStunSelf ? getParryBreakLook() : 'now'
        if (breakLook === 'now') sfx.push('parry')
        else {
          sfx.push('exec_weapon_break')
          fx.push({ kind: 'parry_break', x: p.x + bladeDir * 22, y: p.y - 110, t: time, dir: bladeDir })
          if (breakLook === 'b') fx.push({ kind: 'damage_vignette', t: time })
          // 止めを長く (被弾と同じ「やられた」の手触り)。ab (10-05) は画面の縁の赤みを外し、止めの長さは b のまま。
          if (breakLook === 'b' || breakLook === 'ab') hitStopMs = Math.max(hitStopMs, COMBAT.hit.hitStopMs * 1.5)
        }
        // 火花強度を段で増幅 (5段で +48%、 sparkIntensityMax で頭打ち)。
        const chainBoost = 1 + (chain - 1) * COMBAT.parryChain.sparkBoostPerStep
        // 手応え (hitStop/シェイク) の連鎖倍率。 chain=1 では 1.0 なので単発挙動は不変。
        const tactileChainMul = 1 + (chain - 1) * COMBAT.parryChain.tactileBoostPerStep
        // 崩した瞬間 (10-06「2はA」)：完璧な弾きでボスの体幹が尽きる時は、成功の光の代わりに強い光 (画面が金に染まる＋寄る) を出す。
        //   条件は下の staggerBoss と同じ (b.posture は上で更新済み)。
        const brokeNow = isJust && b.posture >= b.maxPosture && b.state !== 'staggered'
        const downFx = brokeNow && getParryDownLook() === 'z'
        //   chain・weight = 音の候補 (DEV ?parryPitch=・src/parry_pitch.ts) が連続の段と技の重さで音を変えるために運ぶ (描き方は変えない)。
        //   attackId = 音の作り直し (DEV ?parrySfx=・src/parry_material.ts) が技の得物の材質と攻撃力を引くために運ぶ。
        if (breakLook === 'now' && downFx) fx.push({ kind: 'parry_down', x: p.x + bladeDir * 22, y: p.y - 110, t: time,
          chain, weight: next.parryIntensity, attackId: next.attackId })
        else if (breakLook === 'now') fx.push({ kind: 'parry_spark', x: p.x + bladeDir * 22, y: p.y - 110, t: time,
          intensity: Math.min(COMBAT.parryChain.sparkIntensityMax, next.parryIntensity * chainBoost), isJust,
          chain, weight: next.parryIntensity, broke: brokeNow, attackId: next.attackId })
        // ジャスト: フル手応え / 通常: 65% に抑えて「受けた感」と差をつける
        const parryFeedbackMul = isJust ? 1.0 : 0.65
        //   止めは前の長さ (10-16 ユーザー「ヒットストップの長さは前の長さにしてほしい」= 連撃のテンポ)。 見比べの案だけ変わる
        //   (src/parry_tehon_fx.ts・DEV ?parryLook=tehon-v1 は 50ms で頭打ち・tehon-nostop|tehon-yori は止めなし)。
        hitStopMs = Math.max(hitStopMs, tehonParryStopMs(
          Math.round(COMBAT.parrySuccess.hitStopMs * (0.7 + 0.6 * next.parryIntensity) * tactileChainMul * parryFeedbackMul)))
        shake = strongerShake(shake, shakeFrom({
          amplitude: COMBAT.parrySuccess.shake.amplitude * (0.6 + 0.8 * next.parryIntensity) * tactileChainMul * parryFeedbackMul,
          durationMs: COMBAT.parrySuccess.shake.durationMs }, time))
        // 崩しは弾き (パリィ) で起きる。 パリィは1種類 (2026-10-10・isJust は常に真) なので、
        //   弾いて相手の体幹が満ちたら、その場で崩れる。 旧の「通常パリィは崩せない」は ?parryOne=off の時だけ。
        if (isJust && b.posture >= b.maxPosture && b.state !== 'staggered') {
          b = staggerBoss(b)
          sfx.push('stagger')
          shake = strongerShake(shake, shakeFrom(COMBAT.stagger.shake, time))
          // 崩した瞬間の止めを少し延ばす比較版 (DEV ?breakStop=a|b・src/break_stop_fx.ts)。既定は 0 = 今まで通り。
          hitStopMs += breakStopExtraMs()
        }
      } else if (p.state === 'guarding' && next.parryable && (isSwingHit(next) || next.airWave !== undefined)) {
        // (空気の衝撃波も、弾ける白い攻撃なのでガードで受けられる。3つ目でガードが崩れる。)
        // --- ガード成立 (トグル・spec 2): 弾けなかったパリィ可能な近接ヒットを
        //     「HPは減らさず体幹だけで受け止める」(2026-09-04 ユーザー裁定・ELDEN RING の盾受けを体幹に落とし込む)。
        //     ヒットを消費しガードは継続。
        //   ★ 貫通 (spec 3): 弾けない近接 (parryable===false=赤黒い呪いオーラ) はこの枝に入らず、
        //     下の被弾でフルダメージ (裁定C「弾けない攻撃の答えは位置取り/回避」を壊さない)。
        //   ★ ゾーン/設置 (spec 4): follow!=='boss' なのでこの枝に入らず従来どおり被弾。
        //   ★ ボス体幹は増やさない (spec 7・崩し門はジャストパリィ限定=b を一切触らない)。
        //   ★ フラグOFFでは 'guarding' 状態に入らないため、この枝は決して踏まれない (既存挙動不変)。
        const chipHp = Math.ceil(playerIncomingDamage(next.damage, { bossFinisher: next.bossFinisher }) * GUARD.chipDamageMul)
        const hpFloor = b.protectFromDeath ? 1 : 0
        const newHp = Math.max(hpFloor, p.hp - chipHp)
        // 体幹ゼロから2発を受け、3発目で崩れる（初演も同じ回数）。
        const selfGain = guardSelfPostureGain()
        const willStunSelf = (p.posture + selfGain) >= playerMaxPosture()
        const newPosture = Math.min(playerMaxPosture(), p.posture + selfGain)
        playlogRecv('guard', next, p, b, time, { dmg: p.hp - newHp, post: selfGain, broke: willStunSelf })  // 試遊の記録 (見るだけ)
        // chipDamageMul=0 の現在この枝は成立しないが、0.3 へ戻した時のために残す (無敵ではない・削られたら死ぬ)。
        if (newHp <= 0 && p.hp > 0) killedByAttackId = next.attackId
        p = { ...p, hp: newHp, posture: newPosture,
          // のけぞりなし (spec 2) = 被弾フリンチ (hurtVisual/hurtNonce) は出さない。 連続弾きチェーンは途切れさせる。
          parryChain: 0, lastParryTime: null,
          // 被弾より長いロックを掛ける (2026-09-04)。連続で受けると体幹が戻らない = 甘えた受けへの罰。
          postureRegenLockSec: Math.max(p.postureRegenLockSec, GUARD.postureRegenLockSec) }
        if (willStunSelf && p.state !== 'stunned') { p = stunPlayer(p, 'guard'); b = rememberStunSource(b) }   // 体幹満タン→崩れ (spec 6・ガードは崩れを防がない)
        next = { ...next, consumed: true }
        // 主人公専用の受け演出。ボスの guard_spark を流用せず、押し込まれる絵だけを別 FX に持つ。
        // 受け身 (10-21) の半歩の後退は下の knockbackRemainingPx。 前の版 (?ukemi=off) は player.x を動かさず、render 側がこのイベントを短い後退の絵へ変換する。
        sfx.push('guard_player')
        if (ukemiOn() && !willStunSelf) {
          // 受け身 (10-21・src/ukemi.ts): 腕と肩で押し込まれて半歩すべる (押される量は小さく)。 後ろ足に擦り跡。
          //   重い技 (10-22) は重いパリィより大きく押し負ける (後ろ足に土煙)。
          const away = p.x >= b.x ? 1 : -1
          const gk = ukemiGuardKind(next.parryIntensity)
          p = { ...p, knockbackRemainingPx: away * ukemiGuardPushPx(gk) }
          fx.push(...ukemiDustFx(gk, p.x, p.y, away, time))
        }
        fx.push({ kind: 'player_guard_impact', x: p.x, y: p.y - 60, t: time,
          dir: p.facing === 'right' ? 1 : -1, postureRatio: newPosture / playerMaxPosture() })
        // 既存上限(0.6倍)は超えず、軽い攻撃だけ短くして重さを聞かせ分ける。
        const guardWeight = Math.min(1, next.damage / 30)
        hitStopMs = Math.max(hitStopMs, Math.round(COMBAT.hit.hitStopMs * 0.6 * (0.35 + 0.65 * guardWeight)))
        shake = strongerShake(shake, shakeFrom({ amplitude: 3, durationMs: 120 }, time))
      } else if (isPlayerInIFrames(p)) {
        // --- 回避 i-frame: 無傷。重なって凌いだ瞬間に反撃窓 (見切り)。
        //     ただし反撃窓が開くのは「弾けない melee」限定 (next.parryable===false && follow==='boss')。
        //     parryable は弾く対象 / ゾーンは位置どり対象 = 回避しても見切り報酬を与えない。
        //     こうしないと「全部回避→反撃」が安定択になり 3 すくみ (弾き/見切り/位置どり) が壊れる。 ---
        if (!next.counterAwarded && !next.playerWasHit && !next.parryable && isSwingHit(next)) {
          playlogRecv('mikiri', next, p, b, time)  // 試遊の記録 (見るだけ)
          p = openCounterWindow(p)
          counterOpened = true
          sfx.push('perfect_dodge')
          fx.push({ kind: 'perfect_dodge', x: p.x, y: p.y - PLAYER.height / 2, t: time, dir: p.facing === 'right' ? 1 : -1 })
          // Retain dodge reward/FX without interrupting the adopted rush.
          if (!skipsAwokenRushContactStop(b, next)) {
            hitStopMs = Math.max(hitStopMs, 70)
          }
          next = { ...next, counterAwarded: true }
        }
        playlogRecv('dodge', next, p, b, time)  // 試遊の記録 (見るだけ・判定1つにつき1回)
        // melee (ボスの一振り) は時間上の離散イベント = 回避で凌いだら「使い切り」。
        //   消費しないと、 active 突入前に回避を入れた時 i-frame 切れ後の判定の尻尾に
        //   当たる (突進で距離も詰められる) = 「避けたのに当たる」理不尽。
        // 凍結ゾーンは空間の持続なので i-frame は一時しのぎ = 消費しない (位置で離れて避ける)。
        if (isSwingHit(next)) next = { ...next, consumed: true }
        // 比較版 ?dodgeFloor=spend: 無敵で凌いだ床の帯は、その回避で使い切る (配布版は使い切らない = 位置で離れて避ける)。
        if (dodgeFloorSpendsOnIFrame(next)) next = { ...next, dodgeSpent: true }
        // 空気の衝撃波も前縁が触れた一瞬の当たり = 無敵で抜けたらもう当たらない。
        if (next.airWave) next = { ...next, consumed: true }
        // 拍どおりに届く怨念も同じ: 回避の無敵で抜けたら、もう当たらずに飛び去る (転がる向きで当たり外れが出ないように)。
        if (next.spirit?.arriveMs !== undefined && !next.reflected) next = { ...next, spirit: { ...next.spirit, spent: true } }
      } else if (next.grab) {
        // --- 掴み成立 (処刑人 D8/D9): 通常の被ダメの代わりに 'grabbed' へ拘束。
        //     ダメージはこの時点では与えない (holdMs 満了時に player.ts が適用する)。
        //     位置吸着は executeAnchor 方式のテンプレに倣い、boss の向いてる側 (+60px) へ
        //     anchorEaseMs (=EXECUTE.anchorEaseMs=120ms) で smoothstep 補間 (player.ts 側で実施)。
        const dir = b.facing === 'right' ? 1 : -1
        playlogRecv('grab', next, p, b, time)  // 試遊の記録 (見るだけ)
        p = grabPlayer(p, {
          holdMs: next.grab.holdMs,
          damage: next.grab.damage,
          releaseKnockbackPx: next.grab.releaseKnockbackPx,
          pullMs: next.grab.pullMs,
          anchorX: b.x + dir * (next.grab.anchorFwdPx ?? 60),
          anchorFromX: p.x,
          dir,
        })
        const grabSound = grabStartSfx(b.id, next.attackId)  // 花嫁の抱擁の「ぎゅーっ」(11-01・src/finisher_sound.ts)
        if (grabSound) sfx.push(grabSound)
        next = { ...next, consumed: true }
      } else {
        // --- 被弾 ---
        const sinceLast = next.lastHitAtMs == null ? Infinity : elapsed - next.lastHitAtMs
        const canHit = !next.groupSpent && (next.lastHitAtMs == null
          || (!next.hitOnce && sinceLast >= (next.rehitIntervalMs ?? 400)))
        if (canHit) {
          // チュートリアル無敵 (boss.protectFromDeath): melee にもゾーンにも一様に効く
          const hpFloor = b.protectFromDeath ? 1 : 0
          const newHp = (next.playerLethal && !b.protectFromDeath)
            ? 0 : Math.max(hpFloor, p.hp - playerIncomingDamage(next.damage, { bossFinisher: next.bossFinisher }))
          // 怨念・衝撃波 (大暴れ第5案) は体幹に触れない＝当たっても体力だけ (09-20 ユーザー相談)。
          const newPosture = next.spirit ? p.posture : Math.min(playerMaxPosture(), p.posture + next.postureDamage)
          // 死因記録 (P4): このヒットで HP が 0 に落ちた瞬間の attackId を控える。
          if (newHp <= 0 && p.hp > 0) killedByAttackId = next.attackId
          playlogRecv('hit', next, p, b, time, { dmg: p.hp - newHp, post: newPosture - p.posture, lethal: newHp <= 0 })  // 試遊の記録 (見るだけ)
          p = { ...p, hp: newHp, posture: newPosture,
            hurtVisualUntil: time + 0.4, hurtNonce: p.hurtNonce + 1,
            parryChain: 0, lastParryTime: null,
            postureRegenLockSec: next.spirit ? p.postureRegenLockSec : Math.max(p.postureRegenLockSec, PLAYER.hitRegenLockSec) }
          next = { ...next, playerWasHit: true, lastHitAtMs: elapsed,
            consumed: next.hitOnce ? true : next.consumed }
          if (next.groupId !== undefined) spentGroups.add(next.groupId)
          sfx.push('hit_player')
          if (next.hitKnockbackPx) {
            const away = p.x >= b.x ? 1 : -1
            p = { ...p, knockbackRemainingPx: away * next.hitKnockbackPx }
          }
          if (next.playerLethal || next.bossFinisher) {
            // ボス処刑 (playerLethal 即死 または bossFinisher 大ダメージ) の命中:
            //   専用打点FX + 重い hitStop/揺れ (BOSS_EXECUTE_FX)。
            const dir = b.facing === 'right' ? 1 : -1
            const finSound = finisherImpactSfx(next.attackId)  // 致命固有の音 (11-01・src/finisher_sound.ts)
            if (finSound) sfx.push(finSound)
            fx.push({ kind: 'boss_execute_impact', x: p.x, y: p.y - 38, t: time, dir },
              { kind: 'damage_vignette', t: time })
            hitStopMs = Math.max(hitStopMs, BOSS_EXECUTE_FX.impactHitStopMs)
            shake = strongerShake(shake, shakeFrom(BOSS_EXECUTE_FX.impactShake, time))
            // 2026-07-01: bossFinisher 命中で stunned は解除する (無限フィニッシャーループ防止)。
            //   ユーザー実プレイで「フィニッシャー後にボスが執拗に付きまとってブルブル」 バグ = 花嫁が
            //   recovery→idle で再び「player.state === 'stunned'」 を検知して brideExecution を
            //   再発動していた (boss.ts の idle 内 finisher トリガー)。 finisher は「崩しを 1 回で消費」
            //   が仕様なので、 命中時に stunned 解除 + posture リセットして 2 回目を防ぐ。
            if (p.state === 'stunned') {
              p = { ...p, state: 'idle', stateElapsed: 0, posture: 0 }
            }
            // 致命が当たった＝崩れの代償は払った。やり直しの予約は捨てる (src/exec_bait.ts の C)。
            if (b.stunEncore) b = { ...b, stunEncore: undefined }
          } else {
            fx.push({ kind: 'hit_spark', x: p.x, y: p.y - 38, t: time, color: 'red' },
              { kind: 'damage_vignette', t: time })
            // 槍突き接触閃光 (visual-only): lanceThrust 命中時に横一閃を追加。
            if (next.attackId === 'lanceThrust') {
              const dir = b.facing === 'right' ? 1 : -1
              fx.push({ kind: 'lance_flash', x: p.x, y: p.y - 60, t: time, dir })
            }
            // Do not clear another hit's stop (e.g. a simultaneous parry or reflected shot).
            if (!skipsAwokenRushContactStop(b, next)) {
              hitStopMs = Math.max(hitStopMs, COMBAT.hit.hitStopMs)
            }
            shake = strongerShake(shake, shakeFrom(COMBAT.hit.shake, time))
          }
          if (p.posture >= playerMaxPosture() && p.state !== 'stunned') { p = stunPlayer(p, 'hit'); b = rememberStunSource(b) }
        }
      }
    }

    // melee の寿命は hit.elapsedMs でなく boss.stateElapsed 時計 + boss.state/currentAttack で切る
    //   (契約2026-07-16 §B): hit.elapsedMs 基準の従来式 (elapsed >= lifeMs) を残すと、
    //   spawn 同tickの +dt 先行分だけ最大1 fixed tick 早く消える。 zone は従来どおり不変。
    const walls = arenaWalls()
    const travelHitFloor = next.follow === 'travel' && next.vy !== undefined && next.y !== undefined
      && next.y >= ARENA.floorY
    const travelOutside = next.follow === 'travel' && next.x !== undefined && next.widthPx !== undefined
      && (next.x + next.widthPx / 2 < walls.leftWall || next.x - next.widthPx / 2 > walls.rightWall
        || travelHitFloor)
    const expired = next.follow === 'boss'
      ? !(b.state === 'active' && b.currentAttack?.id === next.attackId) || (b.stateElapsed * 1000) >= next.activeMs
      : next.follow === 'travel'
        ? next.consumed || travelOutside || spiritExpired(next)
        : elapsed >= lifeMs
    if (!expired) survivors.push(next)
  }

  // 束の先頭側の帯は、後ろの帯が当たる前にもう処理済み。ここで束の残り全部を止める。
  const groupedOut = spentGroups.size === 0 ? survivors
    : survivors.map((h) => h.groupId !== undefined && !h.groupSpent && spentGroups.has(h.groupId)
      ? { ...h, groupSpent: true } : h)
  // 弾かれた回の、この tick で先に処理済みの帯も止める (DEV ?roarParry=1 だけ)。
  const hitsOut = parriedCasts.size === 0 ? groupedOut
    : groupedOut.map((h) => h.parryCastId !== undefined && !h.consumed && parriedCasts.has(h.parryCastId)
      ? { ...h, consumed: true } : h)
  return { player: p, boss: b, hits: hitsOut, sfx, fx, hitStopMs, shake, counterOpened,
    killedByAttackId }
}
